import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../ui'
import { SingleSelect } from '../../../ui/form/SingleSelect'
import { PlusIcon, TrashIcon } from '../../../ui/icons'
import EventRuleEdit from './EventRuleEdit'
import { createEventRule, createFilterRule, createListRule, createWrapperRule, isEventWrapper, isWrapper, getOperatorTypes, RuleEditProps, VariablesContext } from './RuleHelpers'
import { EventRule, Rule, WrapperRule } from '../../../types'
import RuleEdit from './RuleEdit'

export default function WrapperRuleEdit({
    rule,
    root,
    setRule,
    controls,
    depth = 0,
    eventName = '',
    index = 1,
    maxRuleSets = 5,
    headerPrefix,
    headerActions,
    allowJourney,
}: RuleEditProps<WrapperRule>) {
    const { t } = useTranslation()
    const { listConditionsEnabled } = useContext(VariablesContext)

    const handleAddListRule = () => {
        setRule({
            ...rule,
            children: [
                ...rule.children ?? [],
                createListRule(rule),
            ],
        })
    }

    const handleAddEventWrapper = () => {
        const children = rule.children ?? []
        const newRule: EventRule = createEventRule(rule)
        setRule({
            ...rule,
            children: [
                ...children,
                newRule,
            ],
        })
    }

    const handleAddRuleSet = () => {
        const newWrapper = createWrapperRule()
        if (rule.group !== 'root') {
            setRule(createWrapperRule('root', [
                { ...rule },
                newWrapper,
            ]))
        } else {
            setRule({
                ...rule,
                children: [
                    ...rule.children,
                    newWrapper,
                ],
            })
        }
    }

    const handleRemoveWrapper = (children: Rule[], index: number) => {
        const newChildren = children.filter((_, i) => i !== index)
        const child = newChildren[0]
        if (child && isWrapper(child) && child.parent_uuid === root.uuid && newChildren.length <= 1) {
            setRule(child)
        } else {
            setRule({
                ...rule,
                group: rule.group === 'root' ? 'parent' : rule.group,
                children: children.filter((_, i) => i !== index),
            })
        }
    }

    let ruleSet = (
        <div className="rule-set">
            <div className="rule-set-header">
                {isEventWrapper(rule)
                    ? (
                        <EventRuleEdit
                            rule={rule}
                            setRule={setRule}
                            eventName={eventName} />
                    )
                    : (
                        <>
                            {depth === 1 && `${t('rule')} ${index + 1}: `}
                            {headerPrefix ?? t('rule_include_users_matching')}
                            <SingleSelect
                                value={rule.operator}
                                onChange={operator => setRule({ ...rule, operator })}
                                options={getOperatorTypes(t).wrapper}
                                required
                                hideLabel
                                size="small"
                                toValue={x => x.key}
                            />
                            {t('rule_of_the_following')}
                        </>
                    )
                }
                <div style={{ flexGrow: 1 }} />
                {headerActions}
                {controls}
            </div>
            <div className="rule-set-rules">
                {
                    rule.children?.map((child, index, arr) => (
                        <RuleEdit
                            key={index}
                            root={root}
                            rule={child}
                            setRule={child => setRule({
                                ...rule,
                                children: rule.children?.map((c, i) => i === index ? child : c),
                            })}
                            group={rule.group}
                            eventName={rule.value as string}
                            depth={depth + 1}
                            index={index}
                            allowJourney={allowJourney}
                            controls={
                                <Button
                                    size="small"
                                    icon={<TrashIcon />}
                                    variant={isWrapper(child) ? 'secondary' : 'plain'}
                                    onClick={() => handleRemoveWrapper(arr, index)}
                                />
                            }
                        />
                    ))
                }
            </div>
            <div className="rule-set-actions">
                {
                    rule.group !== 'root' && <>
                        <Button
                            size="small"
                            variant="secondary"
                            icon={<PlusIcon />}
                            onClick={() => setRule({
                                ...rule,
                                children: [
                                    ...rule.children ?? [],
                                    createFilterRule(root, rule, rule.group === 'event' ? 'event' : 'user'),
                                ],
                            })}
                        >
                            {
                                rule.group === 'event'
                                    ? t('rule_add_condition')
                                    : t('rule_add_user_condition')
                            }
                        </Button>
                        {
                            rule.group === 'parent' && (
                                <Button
                                    size="small"
                                    variant="secondary"
                                    icon={<PlusIcon />}
                                    onClick={() => handleAddEventWrapper()}
                                >
                                    {t('rule_add_event_condition')}
                                </Button>
                            )
                        }
                        {
                            rule.group === 'parent' && listConditionsEnabled && (
                                <Button
                                    size="small"
                                    variant="secondary"
                                    icon={<PlusIcon />}
                                    onClick={() => handleAddListRule()}
                                >
                                    {t('rule_add_list_condition')}
                                </Button>
                            )
                        }
                        {
                            allowJourney && rule.group === 'parent' && (
                                <Button
                                    size="small"
                                    variant="secondary"
                                    icon={<PlusIcon />}
                                    onClick={() => setRule({
                                        ...rule,
                                        children: [
                                            ...rule.children ?? [],
                                            createFilterRule(root, rule, 'journey'),
                                        ],
                                    })}
                                >
                                    {t('rule_add_journey_condition')}
                                </Button>
                            )
                        }
                    </>
                }
            </div>
            {
                ((depth === 0 || rule.group === 'root') && rule.children.length < maxRuleSets) && (
                    <div className="rule-set-add">
                        <Button
                            size="small"
                            variant="secondary"
                            icon={<PlusIcon />}
                            onClick={() => handleAddRuleSet()}
                        >
                            {t('rule_add_wrapper_condition')}
                        </Button>
                    </div>
                )
            }
        </div>
    )

    if (depth > 0) {
        ruleSet = (
            <div className="rule">
                {ruleSet}
            </div>
        )
    }

    return ruleSet
}
